import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { apiClient } from '../api/apiConfig';
import { CreditCardIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { Transaction } from '../types/finance';
import { Alert, Spinner, Badge, Button } from './ui';
import { Select } from '../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

export interface TransactionsListRef {
  refresh: () => void;
}

interface CategoryOption {
  id: number;
  name: string;
}

interface AccountOption {
  id: number;
  name: string;
}

const getCurrentMonth = () => format(new Date(), 'yyyy-MM');

const formatMonthLabel = (yyyymm: string) => {
  const [year, month] = yyyymm.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return format(date, 'MMM yyyy');
};

const shiftMonth = (yyyymm: string, delta: number): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  const d = new Date(year, month - 1 + delta);
  return format(d, 'yyyy-MM');
};

const getMonthRange = (yyyymm: string) => {
  const [year, month] = yyyymm.split('-').map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0); // last day of month
  return {
    date__gte: format(start, 'yyyy-MM-dd'),
    date__lte: format(end, 'yyyy-MM-dd'),
  };
};

const TransactionsList = forwardRef<TransactionsListRef>((props, ref) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filter state
  const [month, setMonth] = useState(getCurrentMonth());
  const [filterType, setFilterType] = useState<string>('all');
  const [categoryId, setCategoryId] = useState<string>('');
  const [accountId, setAccountId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Dropdown options
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Fetch dropdown options once
  useEffect(() => {
    apiClient.get('/api/categories/?type=budget').then(res => {
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setCategories(data.map((c: any) => ({ id: c.id, name: c.name })));
    }).catch(() => {});
    apiClient.get('/api/accounts/', { params: { is_active: true, limit: 100 } }).then(res => {
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setAccounts(data.map((a: any) => ({ id: a.id, name: a.name })));
    }).catch(() => {});
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { date__gte, date__lte } = getMonthRange(month);
      const params: Record<string, any> = {
        limit: 200,
        ordering: '-date',
        date__gte,
        date__lte,
      };
      if (filterType !== 'all') params.transaction_type = filterType;
      if (categoryId) params.category = categoryId;
      if (accountId) params.account = accountId;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const response = await apiClient.get('/api/transactions/', { params });
      setTransactions(response.data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions.');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [month, filterType, categoryId, accountId, debouncedSearch, refreshKey]);

  useImperativeHandle(ref, () => ({
    refresh: () => setRefreshKey(prev => prev + 1),
  }));

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const getTransactionBadgeVariant = (type: string): 'success' | 'danger' | 'warning' | 'default' => {
    switch (type) {
      case 'income':
        return 'success';
      case 'expense':
        return 'danger';
      case 'transfer':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getFilterButtonStyle = (isActive: boolean, activeColor: string): React.CSSProperties => ({
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    borderRadius: RADIUS,
    backgroundColor: isActive ? activeColor : COLORS.bg.panel,
    color: isActive ? COLORS.bg.panel : COLORS.text.secondary,
    border: isActive ? 'none' : `1px solid ${COLORS.border.default}`,
    cursor: 'pointer',
  });

  const arrowButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: SPACING[4],
    display: 'inline-flex',
    alignItems: 'center',
    color: COLORS.text.secondary,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && <Alert variant="error">{error}</Alert>}

      {/* Filter row: month picker, category, account, search */}
      <div style={{
        display: 'flex',
        gap: SPACING[12],
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        {/* Month picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
          <button style={arrowButtonStyle} onClick={() => setMonth(m => shiftMonth(m, -1))}>
            <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />
          </button>
          <span style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
            minWidth: '90px',
            textAlign: 'center',
          }}>
            {formatMonthLabel(month)}
          </span>
          <button style={arrowButtonStyle} onClick={() => setMonth(m => shiftMonth(m, 1))}>
            <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
          </button>
        </div>

        {/* Category dropdown */}
        <div style={{ minWidth: '140px', flex: '0 1 auto' }}>
          <Select
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            style={{ width: '100%', padding: `${SPACING[4]} ${SPACING[8]}`, fontSize: TYPOGRAPHY.size.sm }}
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </div>

        {/* Account dropdown */}
        <div style={{ minWidth: '140px', flex: '0 1 auto' }}>
          <Select
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            style={{ width: '100%', padding: `${SPACING[4]} ${SPACING[8]}`, fontSize: TYPOGRAPHY.size.sm }}
          >
            <option value="">All Accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        </div>

        {/* Search */}
        <div style={{ flex: 1, minWidth: '150px' }}>
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              display: 'block',
              width: '100%',
              padding: `${SPACING[4]} ${SPACING[12]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.size.sm,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Type filter buttons */}
      <div style={{ display: 'flex', gap: SPACING[8], flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilterType('all')}
          style={getFilterButtonStyle(filterType === 'all', COLORS.accent.default)}
        >
          All
        </button>
        <button
          onClick={() => setFilterType('income')}
          style={getFilterButtonStyle(filterType === 'income', COLORS.status.success)}
        >
          Income
        </button>
        <button
          onClick={() => setFilterType('expense')}
          style={getFilterButtonStyle(filterType === 'expense', COLORS.status.error)}
        >
          Expenses
        </button>
        <button
          onClick={() => setFilterType('transfer')}
          style={getFilterButtonStyle(filterType === 'transfer', COLORS.status.warning)}
        >
          Transfers
        </button>
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h3 style={{
          fontSize: TYPOGRAPHY.size.lg,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          display: 'flex',
          alignItems: 'center',
          margin: 0,
        }}>
          <CreditCardIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8], color: COLORS.accent.default }} />
          Transactions
          <span style={{
            marginLeft: SPACING[8],
            display: 'inline-flex',
            alignItems: 'center',
            padding: `${SPACING[4]} ${SPACING[8]}`,
            borderRadius: '9999px',
            fontSize: TYPOGRAPHY.size.xs,
            fontWeight: TYPOGRAPHY.weight.medium,
            backgroundColor: COLORS.bg.hover,
            color: COLORS.text.primary,
          }}>
            {loading ? '...' : transactions.length}
          </span>
        </h3>
      </div>

      {loading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: SPACING[24],
        }}>
          <Spinner size="md" />
        </div>
      ) : transactions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: `${SPACING[48]} 0`,
          backgroundColor: COLORS.bg.base,
          borderRadius: RADIUS,
        }}>
          <CreditCardIcon style={{ margin: '0 auto', height: '48px', width: '48px', color: COLORS.text.tertiary }} />
          <p style={{
            marginTop: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
          }}>No transactions found</p>
          <p style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary,
          }}>Try adjusting your filters or selecting a different month</p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
        }}>
          <table style={{
            minWidth: '100%',
            backgroundColor: COLORS.bg.panel,
            borderCollapse: 'collapse',
          }}>
            <thead style={{ backgroundColor: COLORS.bg.base }}>
              <tr>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Date</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Description</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Category</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Account</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Type</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'right',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                }}>Amount</th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: COLORS.bg.panel }}>
              {transactions.map((transaction, index) => (
                <tr
                  key={transaction.id}
                  style={{
                    borderTop: index > 0 ? `1px solid ${COLORS.border.default}` : 'none',
                  }}
                >
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.primary,
                  }}>
                    {formatDate(transaction.date)}
                  </td>
                  <td style={{ padding: `${SPACING[12]} ${SPACING[16]}` }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.primary,
                      }}>{transaction.description}</span>
                      {transaction.merchant_name && (
                        <span style={{
                          fontSize: TYPOGRAPHY.size.xs,
                          color: COLORS.text.secondary,
                        }}>{transaction.merchant_name}</span>
                      )}
                      {transaction.pending && (
                        <Badge variant="warning" size="sm" style={{ marginTop: SPACING[4], width: 'fit-content' }}>
                          Pending
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.secondary,
                  }}>
                    {transaction.category_name || '-'}
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.secondary,
                  }}>
                    {transaction.account_name}
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm,
                  }}>
                    <Badge
                      variant={getTransactionBadgeVariant(transaction.transaction_type)}
                      size="sm"
                    >
                      {transaction.transaction_type_display}
                    </Badge>
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                  }}>
                    <span
                      style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.bold,
                        color: transaction.transaction_type === 'income'
                          ? COLORS.status.success
                          : transaction.transaction_type === 'expense'
                          ? COLORS.status.error
                          : COLORS.text.primary,
                      }}
                    >
                      {transaction.transaction_type === 'income' ? '+' : transaction.transaction_type === 'expense' ? '-' : ''}
                      {formatCurrency(transaction.amount)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

export default TransactionsList;
